import { runtimeConfig } from '@/config/runtime-config'
import { parseSseChunk } from '@/features/shared/chat/stream/sse'
import type { RuntimeSseEvent, RuntimeStreamIdentity } from '@/features/shared/chat/types'
import {
  ApiError,
  handleApplicationChannelReauthentication,
  isRefreshablePortalAuthError,
  readApiErrorResponse,
} from '@/lib/api/http-client'
import { refreshPortalSession } from '@/lib/auth/session-refresh'
import { getStoredToken, type PortalKind } from '@/lib/auth/token-storage'

export interface RuntimeChatStreamOptions {
  afterSequence?: number
  eventContractVersion?: string
  headers?: Record<string, string | undefined>
  method?: 'GET' | 'POST'
  onStreamIdentity?: (identity: RuntimeStreamIdentity) => void
  path: string
  payload?: Record<string, unknown>
  portal?: PortalKind
  signal?: AbortSignal
  streamErrorCode?: string
  submissionId?: string
}

const RUNTIME_EVENT_VERSION_V2 = '2.0'
const RUNTIME_RUN_ID_HEADER = 'X-AIHub-Run-ID'
const RUNTIME_CONVERSATION_ID_HEADER = 'X-AIHub-Conversation-ID'
const RUNTIME_EVENT_VERSION_HEADER = 'X-AIHub-Event-Contract-Version'
const RECOVERABLE_STREAM_ERROR_CODES = new Set([
  'app_runtime.stream_interrupted',
  'app_runtime.stream_sequence_gap',
])
export const RUNTIME_STREAM_RETRY_DELAYS_MS = [2_000, 4_000, 8_000, 16_000, 30_000] as const

interface RuntimeStreamState {
  lastSequence: number
  sawTerminal: boolean
  sawV2: boolean
}

export function isRecoverableRuntimeStreamError(error: unknown) {
  return (
    error instanceof ApiError &&
    Boolean(error.errorCode && RECOVERABLE_STREAM_ERROR_CODES.has(error.errorCode))
  )
}

export function resolveApiUrl(path: string) {
  const normalizedBaseUrl = runtimeConfig.apiBaseUrl.endsWith('/')
    ? runtimeConfig.apiBaseUrl.slice(0, -1)
    : runtimeConfig.apiBaseUrl
  const normalizedPath = path.startsWith('/') ? path : `/${path}`

  if (normalizedBaseUrl.startsWith('http')) {
    return `${normalizedBaseUrl}${normalizedPath}`
  }

  return new URL(`${normalizedBaseUrl}${normalizedPath}`, window.location.origin).toString()
}

async function readStreamError(response: Response) {
  return readApiErrorResponse(response)
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === 'object' && !Array.isArray(value))
}

function readText(value: unknown) {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : undefined
}

function readStreamEventDiagnostics(event: RuntimeSseEvent) {
  if (event.event !== 'stream_error' || !isRecord(event.diagnostics)) {
    return undefined
  }

  const providerMessage =
    readText(event.diagnostics.provider_message) ?? readText(event.diagnostics.providerMessage)

  return providerMessage ? { providerMessage } : undefined
}

function makeStreamError(event: RuntimeSseEvent, fallbackErrorCode: string) {
  if (event.event !== 'stream_error') {
    return undefined
  }

  return new ApiError(
    event.error ?? 'Stream error',
    200,
    event,
    event.error_code ?? fallbackErrorCode,
    undefined,
    event.error_source,
    event.error_label,
    readStreamEventDiagnostics(event),
  )
}

function readRuntimeStreamIdentity(response: Response): RuntimeStreamIdentity | undefined {
  const runId = readText(response.headers.get(RUNTIME_RUN_ID_HEADER))
  const conversationId = readText(response.headers.get(RUNTIME_CONVERSATION_ID_HEADER))
  const eventContractVersion = readText(response.headers.get(RUNTIME_EVENT_VERSION_HEADER))

  if (!runId || !conversationId || eventContractVersion !== RUNTIME_EVENT_VERSION_V2) {
    return undefined
  }
  return { conversationId, eventContractVersion, runId }
}

function dispatchRuntimeSseEvents(
  events: RuntimeSseEvent[],
  onEvent: (event: RuntimeSseEvent) => void,
  fallbackErrorCode: string,
  state: RuntimeStreamState,
) {
  for (const event of events) {
    if (event.version === RUNTIME_EVENT_VERSION_V2) {
      state.sawV2 = true
      if (!Number.isInteger(event.sequence) || (event.sequence ?? 0) <= 0) {
        throw new ApiError(
          'Runtime stream event has no valid sequence',
          200,
          event,
          'app_runtime.stream_sequence_gap',
        )
      }
      if ((event.sequence ?? 0) <= state.lastSequence) {
        continue
      }
      if (event.sequence !== state.lastSequence + 1) {
        throw new ApiError(
          'Runtime stream event sequence is not contiguous',
          200,
          event,
          'app_runtime.stream_sequence_gap',
        )
      }
      state.lastSequence = event.sequence
      state.sawTerminal ||= event.terminal === true
    }

    onEvent(event)

    const streamError = makeStreamError(event, fallbackErrorCode)
    if (streamError) {
      throw streamError
    }
  }
}

function buildStreamRequestInit(options: RuntimeChatStreamOptions, requestBody?: string) {
  // user runtime 和未来 admin 预览聊天共用流式读取，但 token 必须由调用方门户决定。
  const portal = options.portal ?? 'user'
  const token = getStoredToken(portal)
  const headers = new Headers({ Accept: 'text/event-stream' })
  const method = options.method ?? 'POST'

  if (method !== 'GET') {
    headers.set('Content-Type', 'application/json')
  }

  if (token) {
    headers.set('Authorization', `Bearer ${token}`)
  }

  for (const [key, value] of Object.entries(options.headers ?? {})) {
    if (value) {
      headers.set(key, value)
    }
  }

  return {
    body: method === 'GET' ? undefined : requestBody,
    headers,
    method,
    signal: options.signal,
  } satisfies RequestInit
}

async function fetchRuntimeChatStream(options: RuntimeChatStreamOptions, requestBody?: string) {
  return fetch(resolveApiUrl(options.path), buildStreamRequestInit(options, requestBody))
}

function makeInterruptedStreamError() {
  return new ApiError(
    'Runtime stream was interrupted',
    200,
    undefined,
    'app_runtime.stream_interrupted',
  )
}

function waitForRuntimeStreamRetry(delayMs: number, signal?: AbortSignal) {
  return new Promise<void>((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('The operation was aborted', 'AbortError'))
      return
    }

    const timeoutId = window.setTimeout(() => {
      signal?.removeEventListener('abort', handleAbort)
      resolve()
    }, delayMs)
    const handleAbort = () => {
      window.clearTimeout(timeoutId)
      reject(new DOMException('The operation was aborted', 'AbortError'))
    }

    signal?.addEventListener('abort', handleAbort, { once: true })
  })
}

async function fetchRuntimeChatStreamBeforeHeaders(
  options: RuntimeChatStreamOptions,
  expectsV2: boolean,
  requestBody?: string,
) {
  const canRetrySubmission = expectsV2 && Boolean(options.submissionId)
  const attemptDelays = canRetrySubmission ? [0, ...RUNTIME_STREAM_RETRY_DELAYS_MS] : [0]

  for (const [attemptIndex, delayMs] of attemptDelays.entries()) {
    if (delayMs > 0) {
      await waitForRuntimeStreamRetry(delayMs, options.signal)
    }
    try {
      const response = await fetchRuntimeChatStream(options, requestBody)
      const shouldRetryServerError =
        canRetrySubmission &&
        response.status >= 500 &&
        response.status <= 599 &&
        !readRuntimeStreamIdentity(response) &&
        attemptIndex < attemptDelays.length - 1

      if (!shouldRetryServerError) {
        return response
      }
      await response.body?.cancel()
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw error
      }
      if (!canRetrySubmission) {
        if (expectsV2) {
          throw makeInterruptedStreamError()
        }
        throw error
      }
    }
  }

  throw new ApiError(
    'Runtime submission retry limit was reached before response headers arrived',
    200,
    undefined,
    'app_runtime.submission_retry_exhausted',
  )
}

export async function dispatchRuntimeChatStream(
  options: RuntimeChatStreamOptions,
  onEvent: (event: RuntimeSseEvent) => void,
) {
  const fallbackErrorCode = options.streamErrorCode ?? 'app_runtime.stream_error'
  const expectsV2 =
    options.eventContractVersion === RUNTIME_EVENT_VERSION_V2 || options.afterSequence !== undefined
  // Retry 必须复用完全相同的执行 payload；不要在每次 POST 前重新读取可变 UI state。
  const requestBody =
    (options.method ?? 'POST') === 'GET' ? undefined : JSON.stringify(options.payload ?? {})
  let response = await fetchRuntimeChatStreamBeforeHeaders(options, expectsV2, requestBody)

  if (response.status === 401) {
    const error = await readStreamError(response)
    if (handleApplicationChannelReauthentication(error, options.portal ?? 'user')) throw error

    if (isRefreshablePortalAuthError(error)) {
      const refreshed = await refreshPortalSession(options.portal ?? 'user', resolveApiUrl)

      if (refreshed) {
        response = await fetchRuntimeChatStreamBeforeHeaders(options, expectsV2, requestBody)
      } else {
        throw error
      }
    } else {
      throw error
    }
  }

  if (!response.ok) {
    const error = await readStreamError(response)
    handleApplicationChannelReauthentication(error, options.portal ?? 'user')
    throw error
  }

  if (expectsV2) {
    const identity = readRuntimeStreamIdentity(response)

    if (identity) {
      options.onStreamIdentity?.(identity)
    }
  }

  if (!response.body) {
    if (expectsV2) {
      throw makeInterruptedStreamError()
    }
    throw new ApiError('Stream response body is empty', response.status)
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  let streamDone = false
  const streamState: RuntimeStreamState = {
    lastSequence: options.afterSequence ?? 0,
    sawTerminal: false,
    sawV2: expectsV2,
  }

  try {
    while (!streamDone) {
      const { done, value } = await reader.read()
      streamDone = done

      if (done) {
        break
      }

      buffer += decoder.decode(value, { stream: true })
      const result = parseSseChunk(buffer)
      buffer = result.rest
      dispatchRuntimeSseEvents(result.events, onEvent, fallbackErrorCode, streamState)
      if (streamState.sawTerminal) {
        return
      }
    }
  } catch (error) {
    if (
      streamState.sawV2 &&
      !(error instanceof ApiError) &&
      !(error instanceof DOMException && error.name === 'AbortError')
    ) {
      throw new ApiError(
        'Runtime stream was interrupted',
        200,
        undefined,
        'app_runtime.stream_interrupted',
      )
    }
    throw error
  }

  buffer += decoder.decode()

  if (buffer.trim().length > 0) {
    dispatchRuntimeSseEvents(
      parseSseChunk(`${buffer}\n\n`).events,
      onEvent,
      fallbackErrorCode,
      streamState,
    )
  }

  if (streamState.sawV2 && !streamState.sawTerminal) {
    throw new ApiError(
      'Runtime stream ended before a terminal event',
      200,
      undefined,
      'app_runtime.stream_interrupted',
    )
  }
}
